import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Users, MapPin, Activity, Briefcase, Target, MessageSquare } from "lucide-react";

export default function SalesRepsSolution() {
  useEffect(() => {
    document.title = "For Sales Reps — LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "Field intelligence for building materials sales reps and rep agencies. Show up to every dealer call with real contractor demand data and channel performance.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="sales-reps-solution-page">
      <section className="relative bg-[#0F2540] text-white border-b-2 border-[#1A365D]">
        <div className="stripes-stars absolute inset-0 opacity-15" aria-hidden="true"/>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-tag text-slate-300">For Sales Reps · Rep Agencies</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-3 leading-[0.95]">
            Walk into every dealer call with <span className="text-[#C62828]">real numbers.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-3xl">
            LumberLoop USA turns your territory into a live dashboard. Which dealers are winning. Which are missing quotes. Where contractors are asking for your line — and where they aren't. Show up prepared.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">Talk to Us <ArrowRight size={16}/></Link>
            <a href="#value" className="btn-ghost-light inline-flex items-center gap-2">What You Get</a>
          </div>
        </div>
        <div className="h-2 stripes-flag" aria-hidden="true"/>
      </section>

      <section id="value" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-3xl">
          <div className="label-tag">Why Reps Love It</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mt-2 text-[#1A365D]">
            The rep tool your <span className="text-[#C62828]">CRM will never be.</span>
          </h2>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: MapPin, title: "Territory Heat Map", body: "Instantly see where contractors are asking about your product line — and where they aren't. Prioritize your route accordingly." },
            { icon: Activity, title: "Dealer Response Scorecard", body: "Which of your dealers respond in 2 hours? Which take 2 days? Bring receipts to the next dealer meeting." },
            { icon: Target, title: "Lost-Reason Insights", body: "See exactly why dealers in your territory are losing quotes — price, availability, timing. Coach on the real problem." },
            { icon: Users, title: "New Dealer Prospecting", body: "Requests from ZIPs with no dealer coverage = a signed conversation waiting to happen." },
            { icon: Briefcase, title: "Manufacturer-Ready Reporting", body: "Quarterly business reviews with your principals become 10x easier when your data lives in the same system as theirs." },
            { icon: MessageSquare, title: "Contractor Feedback", body: "See what contractors are actually saying about your products in their quote requests — market voice, unfiltered." },
          ].map(f => (
            <div key={f.title} className="brutal-card-flat bg-white p-5">
              <div className="w-11 h-11 bg-[#C62828] text-white flex items-center justify-center"><f.icon size={20}/></div>
              <div className="font-heading font-bold uppercase mt-3 text-[#0F2540]">{f.title}</div>
              <p className="mt-2 text-sm text-slate-700">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FB] border-y border-slate-200 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="label-tag">How Reps Get Access</div>
          <h2 className="font-heading text-3xl md:text-4xl font-black uppercase mt-2 text-[#1A365D]">Two paths in.</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="brutal-card bg-white p-6">
              <div className="font-mono-ll text-[10px] tracking-[0.3em] uppercase text-slate-500">Path 01</div>
              <div className="font-heading text-xl font-bold uppercase mt-2 text-[#0F2540]">Through your manufacturer</div>
              <p className="mt-2 text-slate-700 text-sm">If a manufacturer you rep is already on LumberLoop USA, they can add you to their workspace with rep-level access to their dashboards.</p>
              <ul className="mt-4 space-y-2 text-sm">
                {["Territory-scoped access","See only your dealers and lines","No cost to the rep"].map(it => <li key={it} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#C62828] mt-0.5 flex-shrink-0"/><span className="text-slate-700">{it}</span></li>)}
              </ul>
            </div>
            <div className="brutal-card bg-white p-6">
              <div className="font-mono-ll text-[10px] tracking-[0.3em] uppercase text-slate-500">Path 02</div>
              <div className="font-heading text-xl font-bold uppercase mt-2 text-[#0F2540]">Rep agency workspace</div>
              <p className="mt-2 text-slate-700 text-sm">Multi-line rep agencies can subscribe directly to LumberLoop USA for a cross-principal view of their territory — inquire for details.</p>
              <ul className="mt-4 space-y-2 text-sm">
                {["Multi-manufacturer view","Rep team seats","Custom territory reports"].map(it => <li key={it} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#C62828] mt-0.5 flex-shrink-0"/><span className="text-slate-700">{it}</span></li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0F2540] text-white py-20 border-t-2 border-[#1A365D] text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase">Rep smarter. <span className="text-[#C62828]">Win bigger.</span></h2>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2 mt-8">Book a Rep Demo <ArrowRight size={16}/></Link>
        </div>
      </section>
    </div>
  );
}
